import React, { useEffect, useRef } from "react";
import { Form, useFormikContext } from "formik";
import NavigationalButtons from "../Components/NavigationalButtons";
import { Stack, Text } from "@fluentui/react";
import DropDown from "../../../../Forms/DropDown";
import ClientDropdown from "../../../Checklist/Checklists/Forms/ClientDropdown";
import CobrandDropDown from "../../../Checklist/Checklists/Forms/CobrandDropDown";
import CobrandTagsDropdown from "../../../Checklist/Checklists/Forms/CobrandTagsDropdown";
import UnitDropdown from "../../../../DomainShared/UnitDropdown";
import WarningComponent from "../../../../Util/WarningComponent";

const AccessStep = ({ prevOnClick }) => {
    const { isSubmitting, values, setFieldValue } = useFormikContext();

    const accessLevel = values.reportUserDetail?.accessLevel;
    const selectedCobrandId = values.reportUserDetail?.cobrandId;
    const prevCobrandIdRef = useRef(selectedCobrandId);

    const accessLevelOptions = [
        { key: "global", text: "Global" },
        { key: "cobrandtag", text: "Cobrand Tag" },
        { key: "client", text: "Client" },
        { key: "cobrand", text: "Cobrand" },
        { key: "unit", text: "Unit" },
    ];

    // Determine which fields are enabled based on access level
    const isCobrandTagEnabled = accessLevel === "cobrandtag";
    const isClientEnabled = accessLevel === "client";
    const isCobrandEnabled = accessLevel === "cobrand" || accessLevel === "unit";
    const isUnitEnabled = accessLevel === "unit" && !!selectedCobrandId;

    // Clear dependent fields when access level changes
    const handleAccessLevelChange = (newAccessLevel) => {
        // Set the new access level
        setFieldValue("reportUserDetail.accessLevel", newAccessLevel);

        // Clear fields that are not applicable to the new access level
        if (newAccessLevel !== "cobrandtag") {
            setFieldValue("reportUserDetail.coBrandTagID", null);
        }
        if (newAccessLevel !== "client") {
            setFieldValue("reportUserDetail.clientId", null);
        }
        if (newAccessLevel !== "cobrand" && newAccessLevel !== "unit") {
            setFieldValue("reportUserDetail.cobrandId", null);
        }
        if (newAccessLevel !== "unit") {
            setFieldValue("reportUserDetail.clientSchoolID", null);
        }
    };

    // Clear unit when cobrand changes
    useEffect(() => {
        if (prevCobrandIdRef.current !== selectedCobrandId) {
            setFieldValue("reportUserDetail.clientSchoolID", null);
            prevCobrandIdRef.current = selectedCobrandId;
        }
    }, [selectedCobrandId, setFieldValue]);

    return (
        <Form>
            <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: "20px 0" } }}>
                <Stack tokens={{ childrenGap: 15 }} styles={{ root: { maxWidth: 600 } }}>
                    <DropDown
                        name="reportUserDetail.accessLevel"
                        label="Access Level"
                        options={accessLevelOptions}
                        placeholder="Select an access level"
                        onChange={(e, option) => {
                            handleAccessLevelChange(option.key);
                        }}
                    />
                    <Text variant="small" styles={{ root: { marginTop: -10, color: "#605E5C" } }}>
                        Determines the scope of data this user can access in the reporting system.
                        These permissions apply to both the existing reports site and the Client
                        Console.
                    </Text>

                    {isCobrandTagEnabled && (
                        <>
                            <CobrandTagsDropdown
                                name="reportUserDetail.coBrandTagID"
                                label="Cobrand Tag"
                                placeholder="Select a cobrand tag"
                                isClearable
                                isMulti={false}
                            />
                            <Text
                                variant="small"
                                styles={{ root: { marginTop: -10, color: "#605E5C" } }}
                            >
                                Select the cobrand tag this user will have access to.
                            </Text>
                        </>
                    )}

                    {isClientEnabled && (
                        <>
                            <ClientDropdown
                                name="reportUserDetail.clientId"
                                label="Client"
                                placeholder="Select a client"
                                isClearable
                                isMulti={false}
                            />
                            <Text
                                variant="small"
                                styles={{ root: { marginTop: -10, color: "#605E5C" } }}
                            >
                                Select the client this user will have access to.
                            </Text>
                        </>
                    )}

                    {isCobrandEnabled && (
                        <>
                            <CobrandDropDown
                                name="reportUserDetail.cobrandId"
                                label="Cobrand"
                                byNameOrId="id"
                                placeholder="Select a cobrand"
                                isClearable
                                isMulti={false}
                            />
                            <Text
                                variant="small"
                                styles={{ root: { marginTop: -10, color: "#605E5C" } }}
                            >
                                Select the cobrand this user will have access to.
                            </Text>
                        </>
                    )}

                    {accessLevel === "unit" && (
                        <>
                            <UnitDropdown
                                name="reportUserDetail.clientSchoolID"
                                label="Unit"
                                cobrandId={selectedCobrandId}
                                placeholder={
                                    !selectedCobrandId ? "Select a cobrand first" : "Select a unit"
                                }
                                isClearable
                                isMulti={false}
                                isDisabled={!isUnitEnabled}
                            />
                            <Text
                                variant="small"
                                styles={{ root: { marginTop: -10, color: "#605E5C" } }}
                            >
                                {selectedCobrandId
                                    ? "Select the unit this user will have access to."
                                    : "Select a cobrand first to enable unit selection."}
                            </Text>
                            <WarningComponent
                                isMultiline
                                message="The Client Console does NOT currently support unit level permissions, this is only a feature of the old reports site."
                            />
                        </>
                    )}
                    <NavigationalButtons
                        onPrevHandler={prevOnClick}
                        onNextProps={{ type: "submit", text: "Save and Continue" }}
                        loading={isSubmitting}
                    />
                </Stack>
            </Stack>
        </Form>
    );
};

export default AccessStep;
